import { useState, useEffect } from 'react';

import { fetchLocalJSON } from '../../../shared/utils/fetchLocalJSON.js';
import { Logger } from '../../../shared/utils/logger.js';
import { FETCH_STATUS } from '../../../shared/constants/fetchStatus.js';

// import { normalizeProduct } from '../utils/normalizeProduct.js'

const EMPTY_PRODUCTS = [];

const parseBuildingId = idCad => //TODO: use shared util
  typeof idCad === 'string'
    ? idCad.split('-')[0] ?? null
    : null

/**
 * Loads the product collection for a project and exposes its fetch lifecycle.
 *
 * The WordPress project-products endpoint is the primary source. It returns
 * CRM-enriched product arrays grouped by category key. If that endpoint is
 * unavailable or does not return data, the hook falls back to the local
 * `apartments/apartments.json` file in the project data package.
 *
 * Products from every key in `productCategoryKeys` are merged into one list,
 * enriched with a `building_id`/`buildingId` derived from `id_cad` when absent,
 * and sorted by `id_cad`. Loading buildings, interiors, compartments, and other
 * inventory resources is intentionally outside this hook.
 *
 * Missing connection parameters and failure of both product sources result in
 * an empty product list with `status` set to `FETCH_STATUS.ERROR` and
 * `productsError` containing the failure reason.
 *
 * @param {Object} params
 * @param {string|null|undefined} params.dataUrl - Base URL of the local project data package.
 * @param {string[]} params.productCategoryKeys - Response keys whose product arrays should be merged.
 * @param {string|number|null|undefined} params.projectId - Project identifier used by the WordPress endpoint.
 * @param {string|null|undefined} params.siteUrl - Base URL of the WordPress site.
 * @returns {{
 *   data: Object[],
 *   status: 'loading'|'ready'|'error',
 *   error: Error|null,
 * }} Product data and its loading state. The returned object remains stable until the state changes.
 */
export function useProducts({
	dataUrl,
	productCategoryKeys,
	projectId,
	siteUrl,
}) {
	const [state, setState] = useState({
		data: EMPTY_PRODUCTS,
		status: FETCH_STATUS.LOADING,
		error: null,
	});

	useEffect(() => {
		if (!dataUrl || !siteUrl || !projectId) {
			setState({
				data: EMPTY_PRODUCTS,
				status: FETCH_STATUS.ERROR,
				error: new Error('ProjectId or data/site URL is unavailable'),
			});
			Logger.error({
				message: 'ProjectId or data/site URL is unavailable',
				content: `dataUrl: ${dataUrl}, siteUrl: ${siteUrl}, projectId: ${projectId}`,
			});
			return;
		}

		let cancelled = false;
		let productsData = null;

		const timestamp = new Date().getTime(); //Cache-busting for proxy servers
		async function load() {
			try {
				const response = await fetch(
					`${siteUrl}/wp-json/bim-cadaplus/v1/projects/${projectId}/products?_cb=${timestamp}`,
				);
				if (response.ok) {
					productsData = await response.json();
				}
			} catch {
				// endpoint unreachable — fall through to local fallback
			}

			if (!productsData) {
				productsData = await fetchLocalJSON(
					`${dataUrl}apartments/apartments.json`,
					'useProducts: fallback apartments.json',
				);
			}

			if (cancelled) return;

			if (!productsData) {
				setState({
					data: EMPTY_PRODUCTS,
					status: FETCH_STATUS.ERROR,
					error: new Error(
						'Failed to load product data from endpoint and fallback file',
					),
				});
				return;
			}

			const rawProducts = productCategoryKeys.flatMap(key =>
        Array.isArray(productsData[key]) ? productsData[key] : []
      )
			const products = rawProducts.map(raw => {
				// const normalized = normalizeProduct(raw)
				return {
					...raw,
					// ...normalized,
					building_id: raw.building_id ?? parseBuildingId(raw.id_cad),
					buildingId: raw.building_id ?? parseBuildingId(raw.id_cad),
				}
			})
			const productsSorted = [...products].sort((a, b) => (a.id_cad ?? '').localeCompare(b.id_cad ?? ''))

			setState({
				data: productsSorted,
				status: FETCH_STATUS.READY,
				error: null,
			});
		}

		load().catch((err) => {
			if (cancelled) return;
			setState({
				data: EMPTY_PRODUCTS,
				status: FETCH_STATUS.ERROR,
				error: err instanceof Error ? err : new Error(String(err)),
			});
			Logger.error({
				message: 'Failed to load product data from endpoint and fallback file',
				content: err,
			});
		});

		return () => {
			cancelled = true;
		};
	}, [dataUrl, productCategoryKeys, projectId, siteUrl]);

	return state;
}
